import React from 'react'
import { useCurrency } from '../context/CurrencyContext'
import { INVESTMENT_TYPES } from './Investments'

const FREQ_TO_MONTHLY = { monthly: 1, quarterly: 1/3, 'half-yearly': 1/6, yearly: 1/12 }

export default function NetWorth({ loans, emergencyFund, investments, expenses, insurancePolicies, loading }) {
  const { fmt } = useCurrency()

  // ── Assets ──────────────────────────────────────────────────
  const efBalance        = parseFloat(emergencyFund?.current_balance || 0)
  const totalInvestments = investments.reduce((s, i) => s + parseFloat(i.current_value || 0), 0)
  const totalInvested    = investments.reduce((s, i) => s + parseFloat(i.invested_amount || 0), 0)
  const investmentPL     = totalInvestments - totalInvested
  const totalAssets      = efBalance + totalInvestments

  // ── Liabilities ─────────────────────────────────────────────
  const totalLoans       = loans.reduce((s, l) => s + parseFloat(l.balance || 0), 0)
  const totalLiabilities = totalLoans

  // ── Net Worth ───────────────────────────────────────────────
  const netWorth  = totalAssets - totalLiabilities
  const nwColor   = netWorth >= 0 ? '#4a6741' : '#b8441a'
  const totalAll  = totalAssets + totalLiabilities
  const assetPct  = totalAll > 0 ? (totalAssets      / totalAll) * 100 : 50
  const liabPct   = totalAll > 0 ? (totalLiabilities / totalAll) * 100 : 50

  // ── Monthly outflow (for context) ───────────────────────────
  const totalEMI       = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  const totalInsurance = (insurancePolicies || []).reduce((s, p) => s + parseFloat(p.premium_amount || 0) * (FREQ_TO_MONTHLY[p.premium_frequency] || 1), 0)
  const totalExpenses  = (expenses || []).reduce((s, e) => s + parseFloat(e.amount || 0) * (FREQ_TO_MONTHLY[e.frequency] || 1), 0)
  const monthlyOut     = totalEMI + totalInsurance + totalExpenses
  const monthsLiquid   = monthlyOut > 0 ? efBalance / monthlyOut : 0

  if (loading) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
        Loading net worth...
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Hero ── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.75rem 2rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: nwColor }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>Net Worth</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '3rem', fontWeight: '900', color: nwColor, lineHeight: 1, marginBottom: '0.45rem' }}>
              {netWorth < 0 ? '−' : ''}{fmt(Math.abs(netWorth))}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              {netWorth >= 0
                ? `Assets exceed liabilities by ${fmt(netWorth)}`
                : `Liabilities exceed assets by ${fmt(Math.abs(netWorth))}`}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
            {investmentPL !== 0 && investments.length > 0 && (
              <div style={{ background: investmentPL >= 0 ? 'rgba(74,103,65,0.08)' : 'rgba(184,68,26,0.08)', border: `1px solid ${investmentPL >= 0 ? '#4a674130' : '#b8441a30'}`, borderRadius: '6px', padding: '0.5rem 0.85rem', textAlign: 'right' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>Portfolio P&L</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: investmentPL >= 0 ? '#4a6741' : '#b8441a' }}>
                  {investmentPL >= 0 ? '+' : '−'}{fmt(Math.abs(investmentPL))}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: investmentPL >= 0 ? '#4a6741' : '#b8441a' }}>
                  {totalInvested > 0 ? (investmentPL >= 0 ? '+' : '−') + Math.abs((investmentPL / totalInvested) * 100).toFixed(2) + '%' : ''}
                  {' '}📈 {investmentPL >= 0 ? 'Improving' : 'Declining'}
                </div>
              </div>
            )}
            {netWorth < 0 && (
              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#b8441a', background: 'rgba(184,68,26,0.06)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.45rem 0.75rem' }}>
                📉 Net worth is negative
              </div>
            )}
          </div>
        </div>

        {/* Assets vs Liabilities bar */}
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', height: '12px', borderRadius: '99px', overflow: 'hidden', background: 'var(--surface-hdr)' }}>
            <div style={{ height: '100%', width: assetPct + '%', background: '#4a6741', transition: 'width 0.8s ease', borderRadius: totalAll > 0 && liabPct < 1 ? '99px' : '99px 0 0 99px' }} />
            <div style={{ height: '100%', width: liabPct + '%', background: '#b8441a', transition: 'width 0.8s ease', borderRadius: totalAll > 0 && assetPct < 1 ? '99px' : '0 99px 99px 0' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', fontFamily: 'monospace', fontSize: '0.65rem' }}>
          <span style={{ color: '#4a6741' }}>● Assets {assetPct.toFixed(1)}%</span>
          <span style={{ color: '#b8441a' }}>● Liabilities {liabPct.toFixed(1)}%</span>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
        {[
          { label: 'Total Assets',      value: totalAssets,      color: '#4a6741', sub: `EF + ${investments.length} investment${investments.length !== 1 ? 's' : ''}` },
          { label: 'Total Liabilities', value: totalLiabilities, color: '#b8441a', sub: `${loans.length} outstanding loan${loans.length !== 1 ? 's' : ''}` },
          { label: 'Net Worth',         value: Math.abs(netWorth), color: nwColor, sub: netWorth >= 0 ? '✓ Positive balance sheet' : '⚠ Negative balance sheet', signedValue: true },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: c.color }}>
              {c.signedValue && netWorth < 0 ? '−' : ''}{fmt(c.value)}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Balance Sheet columns ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

        {/* Assets */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'rgba(74,103,65,0.1)', borderBottom: '1px solid var(--border)', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#4a6741' }}>Assets</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#4a6741' }}>{fmt(totalAssets)}</span>
          </div>

          {/* Emergency fund row */}
          <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--surface-hdr)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1rem' }}>🏦</span>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text)', fontWeight: '500' }}>Emergency Fund</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                  {monthlyOut > 0 ? monthsLiquid.toFixed(1) + ' months covered' : 'liquid savings'}
                </div>
              </div>
            </div>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#4a6741' }}>{fmt(efBalance)}</span>
          </div>

          {/* Investment rows by type */}
          {INVESTMENT_TYPES.map(t => {
            const typeInvs  = investments.filter(i => i.type === t.value)
            if (typeInvs.length === 0) return null
            const typeTotal = typeInvs.reduce((s, i) => s + parseFloat(i.current_value || 0), 0)
            const typePL    = typeInvs.reduce((s, i) => s + (parseFloat(i.current_value || 0) - parseFloat(i.invested_amount || 0)), 0)
            return (
              <div key={t.value} style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--surface-hdr)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1rem' }}>{t.icon}</span>
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text)', fontWeight: '500' }}>{t.label}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: typePL >= 0 ? '#4a6741' : '#b8441a' }}>
                      {typePL >= 0 ? '+' : '−'}{fmt(Math.abs(typePL))} P&L
                    </div>
                  </div>
                </div>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#4a6741' }}>{fmt(typeTotal)}</span>
              </div>
            )
          })}

          {totalInvestments === 0 && efBalance === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.72rem' }}>
              No assets recorded yet
            </div>
          )}

          <div style={{ padding: '0.9rem 1.25rem', background: 'rgba(74,103,65,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4a6741', fontWeight: '600' }}>Total Assets</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '900', color: '#4a6741' }}>{fmt(totalAssets)}</span>
          </div>
        </div>

        {/* Liabilities */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'rgba(184,68,26,0.08)', borderBottom: '1px solid var(--border)', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#b8441a' }}>Liabilities</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#b8441a' }}>{fmt(totalLiabilities)}</span>
          </div>

          {loans.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.72rem' }}>
              No outstanding loans 🎉
            </div>
          ) : (
            loans.map(loan => (
              <div key={loan.id} style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--surface-hdr)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text)', fontWeight: '500' }}>{loan.name}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    {loan.type && <span style={{ textTransform: 'capitalize' }}>{loan.type.replace(/_/g,' ')}</span>}
                    {loan.interest_rate && <span> · {loan.interest_rate}% p.a.</span>}
                    {loan.emi_amount    && <span> · EMI {fmt(loan.emi_amount)}</span>}
                  </div>
                </div>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#b8441a', flexShrink: 0, marginLeft: '0.75rem' }}>
                  {fmt(loan.balance)}
                </span>
              </div>
            ))
          )}

          <div style={{ padding: '0.9rem 1.25rem', background: 'rgba(184,68,26,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b8441a', fontWeight: '600' }}>Total Liabilities</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '900', color: '#b8441a' }}>{fmt(totalLiabilities)}</span>
          </div>
        </div>
      </div>

      {/* ── Debt coverage bars ── */}
      {totalLiabilities > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Asset Coverage vs Debt</span>
          </div>
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              { label: 'Investments vs Debt', value: totalInvestments, color: '#6b4c9a' },
              { label: 'Emergency Fund vs Debt', value: efBalance,     color: '#c9a84c' },
              { label: 'All Assets vs Debt',     value: totalAssets,   color: '#4a6741' },
            ].map(item => {
              const pct    = totalLiabilities > 0 ? Math.min(100, (item.value / totalLiabilities) * 100) : 0
              const isOver = item.value >= totalLiabilities
              return (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.85rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(item.value)}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: isOver ? '#4a6741' : '#b8441a' }}>{pct.toFixed(1)}% of debt</span>
                    </div>
                  </div>
                  <div style={{ height: '6px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: isOver ? '#4a6741' : item.color, borderRadius: '99px', transition: 'width 0.8s ease' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Monthly outflow context ── */}
      {monthlyOut > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Monthly Outflow Context</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#b8441a' }}>{fmt(monthlyOut)}<span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: '400' }}>/mo</span></span>
          </div>
          <div style={{ padding: '1.1rem 1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            {[
              { label: 'Loan EMIs',        value: totalEMI,       color: '#b8441a' },
              { label: 'Insurance/Month',  value: totalInsurance, color: '#3d4a5c' },
              { label: 'Living Expenses',  value: totalExpenses,  color: '#c9a84c' },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>{item.label}</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: item.color }}>{fmt(item.value)}</div>
              </div>
            ))}
            <div style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch' }} />
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Months Liquid</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: monthsLiquid >= 6 ? '#4a6741' : monthsLiquid >= 3 ? '#c9a84c' : '#b8441a' }}>
                {monthsLiquid.toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Insights ── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem' }}>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', marginBottom: '0.75rem' }}>Insights</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {netWorth < 0 && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#b8441a', background: 'rgba(184,68,26,0.06)', border: '1px solid rgba(184,68,26,0.18)', borderRadius: '6px', padding: '0.6rem 0.9rem', lineHeight: 1.5 }}>
              ⚠ Net worth is negative. Focus on paying off high-interest loans while steadily growing investments.
            </div>
          )}
          {totalInvestments > 0 && totalInvestments >= totalLoans && totalLoans > 0 && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#4a6741', background: 'rgba(74,103,65,0.06)', border: '1px solid rgba(74,103,65,0.18)', borderRadius: '6px', padding: '0.6rem 0.9rem', lineHeight: 1.5 }}>
              ✓ Your investments ({fmt(totalInvestments)}) cover your total debt ({fmt(totalLoans)}). You could theoretically clear all loans using your portfolio.
            </div>
          )}
          {investmentPL > 0 && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#4a6741', background: 'rgba(74,103,65,0.06)', border: '1px solid rgba(74,103,65,0.18)', borderRadius: '6px', padding: '0.6rem 0.9rem', lineHeight: 1.5 }}>
              📈 Portfolio is in profit ({fmt(investmentPL)} gain). Your net worth is improving as investments grow.
            </div>
          )}
          {efBalance > 0 && investments.length > 0 && totalAssets > 0 && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.6rem 0.9rem', lineHeight: 1.5 }}>
              ℹ Emergency fund is {((efBalance / totalAssets) * 100).toFixed(1)}% of total assets. Investment portfolio is {((totalInvestments / totalAssets) * 100).toFixed(1)}%.
            </div>
          )}
          {investments.length === 0 && efBalance === 0 && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.6rem 0.9rem', lineHeight: 1.5 }}>
              ℹ Add investments and build your emergency fund to strengthen your balance sheet.
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
